import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { Api } from 'Utils/api';
import { pusherSelector } from 'Containers/Core/selectors';
import { useUser } from 'Context/User';

import { SupportConversation, SupportMessage, SupportCategory, SupportView } from './types';

interface SupportContextValue {
  isOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  view: SupportView;
  activeConversationId: number | null;
  openConversation: (id: number) => void;
  goBack: () => void;
  conversations: SupportConversation[];
  activeConversation: { conversation: SupportConversation; messages: SupportMessage[] } | null;
  categories: SupportCategory[];
  totalUnreadCount: number;
  loading: boolean;
  sendingMessage: boolean;
  fetchConversations: () => void;
  fetchConversation: (id: number) => void;
  createConversation: (subject: string, body: string, categoryId?: number) => Promise<SupportConversation | null>;
  sendMessage: (conversationId: number, body: string) => Promise<SupportMessage | null>;
  markAsRead: (conversationId: number) => void;
  closeConversation: (conversationId: number) => void;
  startNewConversation: () => void;
}

const SupportContext = createContext<SupportContextValue | undefined>(undefined);

export function useSupportPanel(): SupportContextValue {
  const ctx = useContext(SupportContext);
  if (!ctx) throw new Error('useSupportPanel must be used within SupportProvider');
  return ctx;
}

export function useSupportProvider(): SupportContextValue {
  const user = useUser();
  const pusher = useSelector(pusherSelector);
  const mounted = useRef(true);

  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<SupportView>('list');
  const [activeConversationId, setActiveConversationId] = useState<number | null>(null);
  const [conversations, setConversations] = useState<SupportConversation[]>([]);
  const [activeConversation, setActiveConversation] = useState<{
    conversation: SupportConversation;
    messages: SupportMessage[];
  } | null>(null);
  const [categories, setCategories] = useState<SupportCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    []
  );

  const totalUnreadCount = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  const fetchConversations = useCallback(async () => {
    try {
      setLoading(true);
      const response = await Api.get('/support/conversations', { params: { sort: '-last_message_at' } });
      if (mounted.current) {
        setConversations(response?.data?.data || []);
      }
    } catch {
      // silently fail
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  const fetchConversation = useCallback(async (id: number) => {
    try {
      setLoading(true);
      const response = await Api.get(`/support/conversations/${id}`);
      if (mounted.current && response?.data) {
        const conv = response.data.data || response.data;
        const messages = conv.messages?.data || conv.messages || [];
        setActiveConversation({ conversation: conv, messages });
      }
    } catch {
      // silently fail
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await Api.get('/support/categories');
      if (mounted.current) {
        setCategories(response?.data?.data || []);
      }
    } catch {
      // silently fail
    }
  }, []);

  const createConversation = useCallback(
    async (subject: string, body: string, categoryId?: number): Promise<SupportConversation | null> => {
      try {
        setSendingMessage(true);
        const payload: any = { subject, body };
        if (categoryId) payload.category_id = categoryId;
        const response = await Api.post('/support/conversations', payload);
        if (mounted.current) {
          const conv = response?.data?.data || response?.data;
          fetchConversations();
          return conv;
        }
        return null;
      } catch {
        return null;
      } finally {
        if (mounted.current) setSendingMessage(false);
      }
    },
    [fetchConversations]
  );

  const sendMessage = useCallback(
    async (conversationId: number, body: string): Promise<SupportMessage | null> => {
      try {
        setSendingMessage(true);
        const response = await Api.post(`/support/conversations/${conversationId}/messages`, { body });
        if (mounted.current) {
          const msg = response?.data?.data || response?.data;
          // Refresh conversation to get latest messages
          fetchConversation(conversationId);
          fetchConversations();
          return msg;
        }
        return null;
      } catch {
        return null;
      } finally {
        if (mounted.current) setSendingMessage(false);
      }
    },
    [fetchConversation, fetchConversations]
  );

  const markAsRead = useCallback(async (conversationId: number) => {
    try {
      await Api.post(`/support/conversations/${conversationId}/messages/read`, {});
      if (mounted.current) {
        setConversations((prev) =>
          prev.map((c) => (c.id === conversationId ? { ...c, unread_count: 0 } : c))
        );
      }
    } catch {
      // silently fail
    }
  }, []);

  const closeConversation = useCallback(
    async (conversationId: number) => {
      try {
        await Api.post(`/support/conversations/${conversationId}/close`, {});
        if (mounted.current) {
          fetchConversations();
          if (activeConversationId === conversationId) {
            fetchConversation(conversationId);
          }
        }
      } catch {
        // silently fail
      }
    },
    [activeConversationId, fetchConversation, fetchConversations]
  );

  const openPanel = useCallback(() => {
    setIsOpen(true);
    fetchConversations();
    fetchCategories();
  }, [fetchConversations, fetchCategories]);

  const closePanel = useCallback(() => {
    setIsOpen(false);
    setView('list');
    setActiveConversationId(null);
    setActiveConversation(null);
  }, []);

  const openConversation = useCallback(
    (id: number) => {
      setActiveConversationId(id);
      setView('chat');
      fetchConversation(id);
    },
    [fetchConversation]
  );

  const goBack = useCallback(() => {
    setView('list');
    setActiveConversationId(null);
    setActiveConversation(null);
    fetchConversations();
  }, [fetchConversations]);

  const startNewConversation = useCallback(() => {
    setView('new');
  }, []);

  // Pusher real-time subscriptions
  useEffect(() => {
    if (!pusher || !user?.id) return undefined;

    const channelName = `support.user.${user.id}`;
    const channel = pusher.subscribe(channelName);

    const handleUpdate = () => {
      fetchConversations();
      if (activeConversationId) {
        fetchConversation(activeConversationId);
      }
    };

    channel.bind('SupportMessageCreated', handleUpdate);
    channel.bind('SupportConversationUpdated', handleUpdate);

    return () => {
      channel.unbind('SupportMessageCreated', handleUpdate);
      channel.unbind('SupportConversationUpdated', handleUpdate);
      pusher.unsubscribe(channelName);
    };
  }, [pusher, user?.id, activeConversationId, fetchConversations, fetchConversation]);

  return {
    isOpen,
    openPanel,
    closePanel,
    view,
    activeConversationId,
    openConversation,
    goBack,
    conversations,
    activeConversation,
    categories,
    totalUnreadCount,
    loading,
    sendingMessage,
    fetchConversations,
    fetchConversation,
    createConversation,
    sendMessage,
    markAsRead,
    closeConversation,
    startNewConversation,
  };
}

export { SupportContext };
